/**
 * Recording a death, and correcting one — DECISIONS.md #53.
 *
 * Mirrors `api/src/Continuum.Api/Deaths/DeathEndpoints.cs`'s two routes and the codes `DeathContent`
 * and `DeathWriteService` return.
 *
 * **There is no `pronouncedBy` in the body.** The server names the signed-in physician on a first
 * version and keeps whoever established the death on a correction; a field it would have to distrust
 * is not sent.
 */

import { unauthenticated } from '../auth/sessionExpiry'

/** The three answers to whether the police were told. None is a default. */
export const POLICE_NOTIFICATIONS = ['notified', 'not-notified', 'not-assessed'] as const

export type PoliceNotification = (typeof POLICE_NOTIFICATIONS)[number]

/** What the death form submits. Mirrors `DeathRequest`. */
export interface DeathSubmission {
  /** The `version_id` being corrected, or absent for a first version. */
  readonly supersedes?: string

  /** `entered-in-error` to withdraw; absent otherwise. */
  readonly status?: 'active' | 'entered-in-error'

  /** The time of death, an ISO instant in UTC — or the typed text, for the server to refuse. */
  readonly diedAt: string
  readonly timeOfDeathEstimated: boolean

  /** When the death was established, likewise. */
  readonly pronouncedAt: string

  /** Null when the physician has not answered; the server refuses it by name. */
  readonly policeNotification: PoliceNotification | null
  readonly note: string | null
}

/** What a successful write returns. Mirrors `WrittenDeath`. */
export interface WrittenDeath {
  readonly deathId: string
  readonly versionId: string
  readonly version: number
  readonly supersedes: string | null
  readonly signatureId: string
}

/**
 * Every code a death write can be refused with — listed, so a code added on the server without
 * wording here is a typecheck failure rather than a blank message.
 */
export const DEATH_REFUSAL_CODES = [
  'DEATH_BODY_MISSING',
  'DEATH_SUPERSEDES_MISSING',
  'DEATH_SUPERSEDES_NOT_ALLOWED',
  'DEATH_STATUS_INVALID',
  'DEATH_STATUS_NOT_ALLOWED',
  'DEATH_TIME_MISSING',
  'DEATH_TIME_MALFORMED',
  'DEATH_TIME_IN_FUTURE',
  'DEATH_PRONOUNCED_BEFORE_DEATH',
  'DEATH_POLICE_NOTIFICATION_INVALID',
  'DEATH_FIELD_TOO_LONG',
  'DEATH_ALREADY_RECORDED',
  'DEATH_NOT_FOUND',
  'DEATH_RETRACTED',
  'DEATH_VERSION_STALE',
  'DEATH_NO_CHANGE',
  'PATIENT_NOT_FOUND',
  'PATIENT_RETRACTED',
  'ROLE_MAY_NOT_ESTABLISH_DEATH',
] as const

export type DeathRefusalCode = (typeof DEATH_REFUSAL_CODES)[number]

/** Params a refusal can carry. */
export interface DeathRefusalParams {
  /** The three time codes: `diedAt` or `pronouncedAt`. */
  readonly field?: string

  /** `DEATH_FIELD_TOO_LONG`. */
  readonly max?: number

  /** `DEATH_ALREADY_RECORDED`: the death that is there. Not displayed. */
  readonly deathId?: string

  readonly supersedes?: string
  readonly current?: string
  readonly roleCode?: string
}

export class DeathRefusedError extends Error {
  readonly code: DeathRefusalCode | 'UNKNOWN'
  readonly params: DeathRefusalParams
  readonly status: number

  constructor(code: DeathRefusalCode | 'UNKNOWN', params: DeathRefusalParams, status: number) {
    super(`Death write refused: ${code}`)
    this.name = 'DeathRefusedError'
    this.code = code
    this.params = params
    this.status = status
  }
}

/** The API could not be reached, or answered something unusable. */
export class DeathUnavailableError extends Error {
  constructor() {
    super('Death write unavailable')
    this.name = 'DeathUnavailableError'
  }
}

function isRefusalCode(value: unknown): value is DeathRefusalCode {
  return typeof value === 'string' && (DEATH_REFUSAL_CODES as readonly string[]).includes(value)
}

async function refusalFrom(response: Response): Promise<DeathRefusedError> {
  try {
    const body = (await response.json()) as {
      error?: { code?: unknown; params?: DeathRefusalParams }
    }

    return new DeathRefusedError(
      isRefusalCode(body.error?.code) ? body.error.code : 'UNKNOWN',
      body.error?.params ?? {},
      response.status,
    )
  } catch {
    return new DeathRefusedError('UNKNOWN', {}, response.status)
  }
}

/**
 * Records a death, or corrects one: one `clinical.death` version, its signature and its audit row, in
 * one transaction on the server. **What this writes cannot be removed** (AGENTS.md §3) — a wrong one
 * is withdrawn by the next version.
 *
 * @param deathId The death being corrected, or null to record a first one.
 */
export async function writeDeath(
  patientId: string,
  deathId: string | null,
  submission: DeathSubmission,
  signal: AbortSignal,
): Promise<WrittenDeath> {
  const url =
    `/api/patients/${encodeURIComponent(patientId)}/deaths` +
    (deathId === null ? '' : `/${encodeURIComponent(deathId)}/versions`)

  let response: Response

  try {
    response = await fetch(url, {
      method: 'POST',
      signal,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(submission),
    })
  } catch (cause) {
    if (signal.aborted) {
      throw cause
    }
    throw new DeathUnavailableError()
  }

  unauthenticated(response)

  if (!response.ok) {
    throw await refusalFrom(response)
  }

  try {
    return (await response.json()) as WrittenDeath
  } catch {
    // Written, and this client cannot say as which version: reopen rather than record it twice.
    throw new DeathUnavailableError()
  }
}
